/* eslint-disable no-continue */
/**
 * An es6 port of google_helper.coffee from the legacy tourbuilder ember app.
 */
class GeocoderResultDecorator {
  city = "";

  // state long name e.g "Georgia"
  stateLabel = "";

  // state code e.g "GA"
  state = "";

  country = "";

  zip = "";

  /**
   * This is Google's value for street. This is not the same as the backend Address street field.
   */
  street = "";

  /**
   * This is what you'd likely want to use interchangeably with backend Address street field.
   * @property {string}
   */
  fullStreet = "";

  streetLong = "";

  streetNumber = "";

  subpremise = "";

  neighborhood = "";

  establishment = "";

  neighborhoodIndex = -1;

  cityIndex = -1;

  result = null;

  searchSpecialSubpremiseWords = [
    "Apt",
    "APT",
    "Building",
    "BUILDING",
    "Unit Bureau",
    "Bureau",
    "BUREAU",
    "floor",
    "Floor",
    "FLOOR",
    "Suite",
    "SUITE",
    "Unit",
    "UNIT",
  ];

  replaceWithSpecialSubpremiseWords = [
    "Apt",
    "Apt",
    "Building",
    "Building",
    "Bureau",
    "Bureau",
    "Bureau",
    "floor",
    "floor",
    "floor",
    "Suite",
    "Suite",
    "Unit",
    "Unit",
  ];

  constructor(result) {
    // prevent mutating the original result.
    this.result = JSON.parse(JSON.stringify(result));

    for (let index = 0; index < result.address_components.length; index += 1) {
      const component = result.address_components[index];

      if (this.isStateComponent(component)) {
        // We normalize (results in unicode letter and unicode accent) and then strip the string of diacritics (remove any unicode accents)
        // For example an accented 'é' in 'Québec' received from Google Maps API (once normalized and stripped of diacritics) can be matched against the non-accented version in Tourbuzz's `nest/classes/Tourbuzz/Data/GeoData.php`
        // Solution sourced from https://stackoverflow.com/questions/990904/remove-accents-diacritics-in-a-string-in-javascript/37511463#37511463
        const diacriticlessLongName = component.long_name.normalize("NFD").replace(/[\u0300-\u036f]/g, "");
        this.stateLabel = diacriticlessLongName.trim();
        this.state = component.short_name.trim();
        continue;
      }

      if (this.isCityComponent(component)) {
        this.city = component.long_name.trim();
        this.cityIndex = index;
        continue;
      }

      if (this.isNeighborhoodComponent(component)) {
        this.neighborhood = component.long_name.trim();
        this.neighborhoodIndex = index;
        continue;
      }

      if (this.isStreetNumberComponent(component)) {
        this.streetNumber = component.long_name.trim();
        continue;
      }

      if (this.isCountryComponent(component)) {
        this.country = component.long_name.trim();

        if (this.country === "United States" || this.country === "Etats-Unis" || this.country === "États-Unis") {
          this.country = "USA";
        }

        continue;
      }

      if (this.isPostalComponent(component)) {
        this.zip = component.long_name.trim();
        continue;
      }

      if (this.isUnitComponent(component)) {
        // We replace some special words at the subpremise returned from Google
        // Please check the arrays searchSpecialSubpremiseWords and replaceWithSpecialSubpremiseWords above
        this.subpremise = component.long_name.trim();

        let specialSubpremiseWordFound = false;
        let addedWord = "";

        for (let i = 0; i < this.searchSpecialSubpremiseWords.length; i += 1) {
          if (this.subpremise.indexOf(this.searchSpecialSubpremiseWords[i]) !== -1) {
            this.subpremise = this.subpremise
              .replace(this.searchSpecialSubpremiseWords[i], this.replaceWithSpecialSubpremiseWords[i])
              .trim();

            specialSubpremiseWordFound = true;

            break;
          }
        }

        // If we were unable to find any special words at the subpremise
        // Add Unit in front of it
        if (!specialSubpremiseWordFound) {
          addedWord = "Unit ";
        }

        this.street2 = `${addedWord}${this.subpremise}`;
        continue;
      }

      if (this.isStreetComponent(component)) {
        this.street = component.short_name.trim();
        this.streetLong = component.long_name.trim();
        continue;
      }

      if (this.isEstablishmentComponent(component)) {
        this.establishment = component.long_name.trim();
        continue;
      }
    }

    this.fullStreet = this.getFullStreet();
  }

  isStateComponent(component) {
    return component.types[0] === "administrative_area_level_1";
  }

  isCityComponent(component) {
    if (component.types.indexOf("locality") !== -1) {
      return true;
    }

    if (component.types.indexOf("sublocality") !== -1) {
      return true;
    }

    return false;
  }

  isNeighborhoodComponent(component) {
    return component.types[0] === "neighborhood";
  }

  isStreetComponent(component) {
    return component.types[0] === "route";
  }

  isStreetNumberComponent(component) {
    return component.types[0] === "street_number";
  }

  isCountryComponent(component) {
    return component.types[0] === "country";
  }

  isPostalComponent(component) {
    return component.types[0] === "postal_code";
  }

  isUnitComponent(component) {
    return component.types[0] === "subpremise";
  }

  isEstablishmentComponent(component) {
    return component.types[0] === "establishment";
  }

  replaceCityWithNeighborhood() {
    if (this.cityIndex !== -1 && this.neighborhoodIndex !== -1) {
      // address components are used to fill out the address fields
      this.result.address_components[this.cityIndex].long_name = this.result.address_components[
        this.neighborhoodIndex
      ].long_name;
      this.result.address_components[this.cityIndex].short_name = this.result.address_components[
        this.neighborhoodIndex
      ].short_name;

      // city attribute is used when generating the full address string
      this.city = this.result.address_components[this.neighborhoodIndex].long_name.trim();
    }
  }

  getFullStreet() {
    let address = `${this.streetNumber} ${this.street}`;

    if (this.establishment !== "") {
      address = `${this.establishment} ${this.streetNumber} ${this.street}`;
    }

    return address.trim();
  }

  getFullAddress() {
    let address = `${this.streetNumber} ${this.streetLong}`;

    if (this.establishment !== "") {
      address = `${this.establishment} ${this.streetNumber} ${this.streetLong}`;
    }

    address = `${address}, ${this.city}, ${this.state} ${this.zip}, ${this.country}`;

    return address.trim();
  }
}

export default GeocoderResultDecorator;
